/**
 * Μορφοποίηση και ετικέτες του παραθύρου «Ταμείο» — οι ίδιες λέξεις και τα ίδια ποσά με το παλιό
 * παράθυρο κλεισίματος / Χ (απόφαση ιδιοκτήτη 6/10/2026: αλλάζει μόνο η οργάνωση της οθόνης, όχι τα
 * νούμερα ούτε η εκτύπωση). Οι ώρες είναι πάντα ώρα Ελλάδας, 24ωρη.
 */
import { formatMoney } from '../../utils/formatMoney';
import type { StaffAction, StaffActionKind } from '../../types/registerReport';
import type { RegisterReportSection } from '../../utils/registerReportSections';

export const euro = (n: number) => formatMoney(Number(n) || 0);

/** Με πρόσημο: +€4.00 / −€4.60 / €0.00. */
export const signedEuro = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${euro(Math.abs(n))}`;

/** Σύνολο που μπορεί να βγει αρνητικό (π.χ. «Μετρητά στο συρτάρι»): €4.00 / −€4.60 — ποτέ «€-4.60». */
export const totalEuro = (n: number) => (Number(n) < -0.005 ? `−${euro(Math.abs(Number(n)))}` : euro(n));

/** 10 → «10», 12.5 → «12,5». */
export const decimal = (n: number) => String(Number(n) || 0).replace('.', ',');

const ATHENS = 'Europe/Athens';

const toDate = (iso: string | null | undefined) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime()) ? d : null;
};

/** Ώρα Ελλάδας 24ωρη («20:05»): το σκέτο el-GR δίνει «08:05 μ.μ.». */
export const athensTime = (iso: string | null | undefined) => {
  const d = toDate(iso);
  return d
    ? d.toLocaleTimeString('el-GR', { timeZone: ATHENS, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    : '--:--';
};

/** «05/10 13:31» — ημέρα/μήνας και ώρα Ελλάδας 24ωρη. */
export const athensDateTime = (iso: string | null | undefined) => {
  const d = toDate(iso);
  return d
    ? `${d.toLocaleDateString('el-GR', { timeZone: ATHENS, day: '2-digit', month: '2-digit' })} ${athensTime(iso)}`
    : '--';
};

export const TYPE_LABELS: Record<string, string> = {
  '11.1': 'Απόδειξη',
  '11.2': 'Απόδ. Υπηρεσίας',
  '1.1': 'Τιμολόγιο',
  '2.1': 'Τιμ. Υπηρεσίας',
  '8.6': 'Δελτίο Παραγγελίας',
  '9.3': 'Δελτίο Αποστολής',
  '11.4': 'Ακυρωτικό',
  '5.1': 'Πιστωτικό',
};

export const PLATFORM_LABELS: Record<string, string> = {
  efood: 'efood',
  wolt: 'Wolt',
  box: 'Box',
  own: 'Κατάστημα',
  internal: 'Κατάστημα',
};

export const CHANNEL_LABELS: Record<string, string> = {
  takeaway: 'Take away',
  delivery: 'Delivery',
  dine_in: 'Dine-in',
};

// Η λίστα «παραγγελίες σε εξέλιξη» που μπλοκάρει το κλείσιμο (409 open_orders).
export const OPEN_PLATFORM_LABELS: Record<string, string> = { ...PLATFORM_LABELS, internal: 'Κατάστημα' };
export const OPEN_STATUS_LABELS: Record<string, string> = {
  pending: 'Σε αναμονή',
  pending_accept: 'Αναμονή αποδοχής',
  accepted: 'Σε εξέλιξη',
  ready: 'Έτοιμη',
  dispatched: 'Σε διανομή',
};
// Καταστάσεις ΤΡΑΠΕΖΙΟΥ (dine_in_sessions.status) στην ίδια λίστα: ο server στέλνει τα ανοιχτά
// τραπέζια με order_type 'dine_in' και την ωμή κατάσταση.
export const OPEN_TABLE_STATUS_LABELS: Record<string, string> = {
  open: 'Ανοιχτό',
  receipt_printed: 'Εκδόθηκε απόδειξη',
  invoice_printed: 'Εκδόθηκε τιμολόγιο',
  paid: 'Πληρωμένο, δεν έκλεισε',
};

// «Κεράσματα & ακυρώσεις»: ετικέτα ανά είδος και τα σύνολα, όπως στην εκτύπωση.
export const STAFF_ACTION_LABELS: Record<StaffActionKind, string> = {
  treat: 'Κέρασμα',
  discount: 'Έκπτωση',
  cancel_item: 'Ακύρωση είδους',
  cancel_order: 'Ακύρωση παραγγελίας',
  cancel_table: 'Ακύρωση τραπεζιού',
  void_payment: 'Ακύρωση πληρωμής',
};

export const STAFF_ACTION_GROUPS: Array<{ key: string; label: string; kinds: StaffActionKind[] }> = [
  { key: 'treats', label: 'Κεράσματα', kinds: ['treat'] },
  { key: 'discounts', label: 'Εκπτώσεις', kinds: ['discount'] },
  { key: 'cancellations', label: 'Ακυρώσεις', kinds: ['cancel_item', 'cancel_order', 'cancel_table'] },
  { key: 'voids', label: 'Ακυρ. πληρωμών', kinds: ['void_payment'] },
];

/** Πλήθος και ποσό ανά ομάδα (Κεράσματα, Εκπτώσεις, Ακυρώσεις, Ακυρ. πληρωμών) — και οι μηδενικές. */
export function staffActionTotals(actions: StaffAction[]) {
  return STAFF_ACTION_GROUPS.map(group => {
    const rows = actions.filter(a => group.kinds.includes(a.kind));
    return { ...group, count: rows.length, value: rows.reduce((s, a) => s + (Number(a.amount) || 0), 0) };
  });
}

/** «Κέρασμα», «Έκπτωση (−12,5%)», «Ακύρωση είδους»… */
export const staffActionLabel = (a: Pick<StaffAction, 'kind' | 'percent'>) => {
  const label = STAFF_ACTION_LABELS[a.kind] || a.kind;
  return a.kind === 'discount' && a.percent != null ? `${label} (−${decimal(a.percent)}%)` : label;
};

/** «2× Μουσακάς (Μερίδα)» — χωρίς ποσότητα όταν δεν υπάρχει (π.χ. ακύρωση πληρωμής). */
export const staffActionWhat = (a: Pick<StaffAction, 'quantity' | 'label'>) =>
  `${a.quantity != null ? `${decimal(a.quantity)}× ` : ''}${a.label || ''}`.trim();

export const NO_STAFF_DATA = 'Δεν υπάρχουν στοιχεία για αυτό το κλείσιμο';

/** Οι ενότητες της ΕΚΤΥΠΩΣΗΣ Ζ/Χ (ρυθμίσεις καταστήματος) — η οθόνη δείχνει πάντα τα πάντα. */
export const PRINT_SECTION_LABELS: Record<RegisterReportSection, string> = {
  classic: 'Κλασικό Ζ',
  users: 'Εισπράξεις χρηστών',
  staff_actions: 'Κεράσματα & ακυρώσεις',
};
